import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { Button, Col, Dropdown, DropdownButton, Form, Row } from 'react-bootstrap';

import useStore from '../../../store';
import { Http } from '../../../support';
import TotalesMain from './components/TotalesMain';
import PeriodoForm from './components/PeriodoForm';
import Complementos from './complementos/Complementos';
import EmisorSelection from './components/EmisorSelection';
import LoadingButton from '../../../facturotopia/LoadingButton';
import ReceptorSelection from './components/ReceptorSelection';
import RetencionBaseForm from './components/RetencionBaseForm';
import ResponseError from '../../../shared/responses/ResponseError';
import ComplementosResumen from './complementos/ComplementosResumen';
import ResponseResult from '../../../shared/responses/ResponseResult';
import RetencionesRequestInputTransformer from '../transformers/RetencionesRequestInputTransformer';

const mapState = (state) => {
  return state.app.retenciones.creation;
};

const mapActions = (actions) => {
  return {
    ...actions.app.retenciones.creation,
    highlight: actions.app.retenciones.list.highlight,
  };
};

const initialSteps = {
  bootstrap: true,
  base: false,
  emisor: false,
  receptor: false,
  periodo: false,
  totales: false,
};

const initialFormValidation = {
  isValidRetencionBase: false,
  isValidEmisor: false,
  isValidReceptor: false,
  isValidPeriodo: false,
  isValidTotales: false,
};

const RetencionCreate = (props) => {
  const [state, actions] = useStore(mapState, mapActions);
  const [steps, setSteps] = useState({ ...initialSteps });
  const [formValidation, setFormValidation] = useState({ ...initialFormValidation });
  const [complemento, setComplemento] = useState('');
  const [complementoEditing, setComplementoEditing] = useState();
  const [data, setData] = useState({});

  useEffect(() => {
    if (!!props.data && Object.keys(data).length === 0) {
      setData({ ...props.data });
      setSteps({
        bootstrap: true,
        base: true,
        emisor: true,
        receptor: true,
        periodo: true,
        totales: true,
      });
      setFormValidation({
        isValidRetencionBase: true,
        isValidEmisor: true,
        isValidReceptor: true,
        isValidPeriodo: true,
        isValidTotales: true,
      });
    }
  }, [props.data]); // eslint-disable-line

  const handleOnChange = (value, metadata = {}) => {
    const newState = {
      ...data,
      ...value,
    };

    if (value.NacionalidadR !== undefined) {
      newState.Receptor = {};
    }

    if (metadata.step) {
      setSteps({
        ...steps,
        [metadata.step]: metadata.isValid,
      });
    }

    if (metadata.name) {
      setFormValidation({
        ...formValidation,
        [metadata.name]: metadata.isValid,
      });
    }

    setData(newState);
  };

  const handleOnEmisorChange = (value) => {
    const { rfc, razon_social } = value.Emisor;
    handleOnChange(value, {
      step: 'emisor',
      name: 'isValidEmisor',
      isValid: !!rfc && !!razon_social,
    });
  };

  const handleOnReceptorChange = (value) => {
    const { Nacional, Extranjero } = value.Receptor;
    handleOnChange(value, {
      step: 'receptor',
      name: 'isValidReceptor',
      isValid: !!Nacional || !!Extranjero,
    });
  };

  const handleOnComplementoChange = (complemento) => {
    const newData = {
      ...data,
      Complemento: {
        ...data.Complemento,
        ...complemento,
      },
    };

    setData(newData);
    setComplemento(null);
    setComplementoEditing(undefined);
  };

  const handleOnSetComplemento = (name) => {
    setComplemento(name);
  };

  const handleOnComplementoEdit = (complemento, values) => {
    const { Complemento = {} } = data;

    handleOnChange({
      ...data,
      Complemento: {
        ...Complemento,
        [complemento]: undefined,
      },
    });

    setComplemento(complemento);
    setComplementoEditing(values);
  };

  const handleOnComplementoDelete = (complemento) => {
    const { Complemento = {} } = data;

    handleOnChange({
      ...data,
      Complemento: {
        ...Complemento,
        [complemento]: undefined,
      },
    });

    setComplemento(null);
  };

  const handleOnCancel = () => {
    props.history.replace('/app/retenciones');
  };

  const handleOnAction = (action) => {
    if (action === 'reset') {
      actions.resetError();

      return;
    }

    actions.resetResult();

    props.history.push('/app/retenciones');

    actions.highlight(result.data);
  };

  const handleDownload = async (uuid) => {
    try {
      const params = {
        type: 'zip',
      };

      const response = await Http.get(`${window.BONJOUR_URL}/api/retenciones/${uuid}/download`, {
        params,
        responseType: 'blob',
      });

      window.downloadFromHttpResponse(response);
    } catch (error) {
      window.ToastManager.show({
        variant: 'error',
        title: 'Error',
        message: error.data?.message || error.message || 'No podemos descargar tu retención',
      });
    } finally {
      handleOnAction('success');
    }
  };

  const handleOnStampingRequest = () => {
    let input = RetencionesRequestInputTransformer.make({ ...data });

    actions.submit(input);
  };

  const renderError = () => {
    return (
      <div className="result-container">
        <ResponseError
          title="Error"
          error={error}
          onAction={handleOnAction}
        />
      </div>
    );
  };

  const renderResult = () => {
    return (
      <div className="result-container">
        <ResponseResult
          title="Factura emitida"
          result={result}
          extra={
            <>
              <Button
                type="button"
                variant="secondary"
                className="mr-3"
                onClick={handleDownload.bind(null, result.data.uuid)}
              >
                Descargar
              </Button>

              <Button
                type="button"
                onClick={handleOnAction.bind(null, 'success')}
              >
                Continuar
              </Button>
            </>
          }
        />
      </div>
    );
  };

  const isValid = () => {
    let isValid = false;

    for (const key in formValidation) {
      if (Object.prototype.hasOwnProperty.call(formValidation, key)) {
        if (formValidation[key] === false) {
          isValid = false;
          break;
        }

        isValid = formValidation[key] ?? false;
      }
    }

    return isValid;
  };

  const { busy, result, error } = state;
  const { MontoTotRet, iva, isr } = data.Totales ?? {};
  const impuestoCompleted = MontoTotRet > 0 ? !!iva || !!isr : true;
  const showComplemento = !!steps.base && !!steps.emisor && !!steps.receptor && !!steps.periodo && !!steps.totales;

  return (
    <>
      {!!error && renderError()}

      {!!result && renderResult()}

      <div className="pb-4">
        <div>
          <strong className="text-black text-upper">Información General</strong>
        </div>

        <div className="section">
          <Form autoComplete="off">
            {!!steps.bootstrap && (
              <RetencionBaseForm
                data={data}
                onFormDataChange={handleOnChange}
              />
            )}
          </Form>
        </div>
      </div>

      <Row>
        <Col
          xs="12"
          sm="6"
          className="pb-4"
        >
          <div>
            <strong className="text-black text-upper">Emisor</strong>
          </div>

          {!!steps.base && (
            <div className="section">
              <EmisorSelection
                data={data}
                onChange={handleOnEmisorChange}
              />
            </div>
          )}
        </Col>

        <Col
          xs="12"
          sm="6"
          className="pb-4"
        >
          <div>
            <strong className="text-black text-upper">Receptor</strong>
          </div>

          {!!steps.base && !!steps.emisor && (
            <div className="section">
              <ReceptorSelection
                data={data}
                onChange={handleOnReceptorChange}
              />
            </div>
          )}
        </Col>
      </Row>

      <Form>
        <div className="pb-4">
          <div>
            <strong className="text-black text-upper">Periodo</strong>
          </div>

          {!!steps.base && !!steps.emisor && !!steps.receptor && (
            <div className="section">
              <PeriodoForm
                data={data.Periodo}
                onFormDataChange={handleOnChange}
              />
            </div>
          )}
        </div>

        <div className="pb-4">
          <div>
            <strong className="text-black text-upper">Totales</strong>
          </div>

          {!!steps.base && !!steps.emisor && !!steps.receptor && !!steps.periodo && (
            <div className="section">
              <TotalesMain
                data={data}
                onFormDataChange={handleOnChange}
              />
            </div>
          )}
        </div>
      </Form>

      <div className="pb-4">
        <div>
          <strong className="text-black text-upper">Complementos</strong>
        </div>

        {showComplemento && !complemento && (
          <div className="section">
            <Row>
              <Col>
                <div className="pb-2">
                  Si deseas agregar un complemento puedes seleccionarlo de la lista de complementos disponibles en la
                  plataforma
                </div>

                <DropdownButton
                  id="dropdown-basic-button"
                  title="Complementos"
                >
                  {/* <Dropdown.Item onClick={ handleOnSetComplemento.bind(null, 'Arrendamientoenfideicomiso') }>
                    Arrendamiento en fideicomiso
                  </Dropdown.Item>
                  */}
                  <Dropdown.Item onClick={handleOnSetComplemento.bind(null, 'Dividendos')}>Dividendos</Dropdown.Item>
                  <Dropdown.Item onClick={handleOnSetComplemento.bind(null, 'EnajenaciondeAcciones')}>
                    Enajenacion de acciones
                  </Dropdown.Item>
                  <Dropdown.Item onClick={handleOnSetComplemento.bind(null, 'Pagosaextranjeros')}>
                    Pagos a extranjeros
                  </Dropdown.Item>
                  <Dropdown.Item onClick={handleOnSetComplemento.bind(null, 'Intereses')}>Intereses</Dropdown.Item>
                  <Dropdown.Item onClick={handleOnSetComplemento.bind(null, 'Intereseshipotecarios')}>
                    Intereses Hipotecarios
                  </Dropdown.Item>
                  <Dropdown.Item onClick={handleOnSetComplemento.bind(null, 'ServiciosPlataformasTecnologicas')}>
                    Plataformas Tecnológicas
                  </Dropdown.Item>
                  <Dropdown.Item onClick={handleOnSetComplemento.bind(null, 'Fideicomisonoempresarial')}>
                    Fideicomiso No Empresarial
                  </Dropdown.Item>
                  {/* <Dropdown.Item onClick={ handleOnSetComplemento.bind(null, 'Intereses') } >
                    Intereses
                  </Dropdown.Item>
                  <Dropdown.Item onClick={ handleOnSetComplemento.bind(null, 'Operacionesconderivados') } >
                    Operaciones con Derivados
                  </Dropdown.Item>
                  <Dropdown.Item onClick={ handleOnSetComplemento.bind(null, 'Planesderetiro') } >
                    Planes de retiro
                  </Dropdown.Item> */}
                </DropdownButton>
              </Col>
            </Row>
          </div>
        )}
      </div>

      {!!complemento && (
        <Row>
          <Col>
            <Complementos
              data={complementoEditing}
              complemento={complemento}
              onChange={handleOnComplementoChange}
            />
          </Col>
        </Row>
      )}

      <Row>
        <Col>
          <ComplementosResumen
            data={data.Complemento}
            onEdit={handleOnComplementoEdit}
            onDelete={handleOnComplementoDelete}
          />
        </Col>
      </Row>

      {!complemento && (
        <Row className="pt-4 pb-5">
          <Col>
            <Button
              variant="secondary"
              className="mr-2"
              disabled={busy}
              onClick={handleOnCancel}
            >
              Cancelar
            </Button>

            <LoadingButton
              loading={busy}
              disabled={!isValid() || busy || !impuestoCompleted}
              onClick={handleOnStampingRequest}
            >
              {busy ? 'Procesando...' : 'Crear factura'}
            </LoadingButton>
          </Col>
        </Row>
      )}
    </>
  );
};

export default withRouter(RetencionCreate);
